import {
	dropDareDifficultyLevelEnum,
	pointsCalculationMethodEnum,
	rewardForOptions,
} from 'root/src/shared/constants/dropEvent'
import {
	urlPattern,
	youtubeUrlPattern,
	emptyRe } from 'root/src/shared/descriptions/formSchemas/addDropEventSchema'
import { RewardType } from 'root/src/shared/rewards/constants'

export const GAME_DESCRIPTION_MAX_LENGTH = 1000

export const DARE_HINT_MAX_LENGTH = 110
export const DARE_DESCRIPTION_MAX_LENGTH = 110
export const LINK_MARKETING_COPY_MAX_LENGTH = 200
// Exported so the wizard can stop the brand at the same number the payload allows. Going over it used
// to break EVERY save (not just publishing) with a raw schema error.
export const SIMILAR_GAMES_MAX_ITEMS = 10

export default {
	type: 'object',
	properties: {
		dropEventPayload: {
			type: 'object',
			properties: {
				dropTitle: {
					type: 'string',
					errorMessage: {
						required: 'Please enter drop title',
					},
				},
				campaignName: {
					type: 'string',
					errorMessage: {
						required: 'Please enter your campaign name',
					},
				},
				gameObj: {
					type: 'object',
					items: {
						type: 'object',
						properties: {
							id: { type: 'integer' },
							label: { type: 'string' },
							value: { type: 'integer' },
							image: { type: 'string' },
						},
						required: ['id'],
					},
					errorMessage: {
						required: 'Please select game',
					},
				},
				liveDate: {
					type: 'string',
					errorMessage: {
						required: 'Please select a campaign start date',
					},
				},
				messageToCreator: { type: 'string' },
				initialProduct: { type: ['array', 'null'] },
				isGameFree: { type: 'boolean' },
				similarGames: {
					type: 'array',
					minItems: 0,
					maxItems: SIMILAR_GAMES_MAX_ITEMS,
					uniqueItems: true,
					items: {
						type: 'object',
						properties: {
							id: { type: 'number' },
							value: { type: 'number' },
							label: { type: 'string' },
							image: { type: 'string' },
						},
						required: ['id'],
					},
					errorMessage: {
						required: 'Please select similar games',
					},
				},
				brandLogoUrl: {
					type: ['string', 'object'],
				},
				assets: {
					type: 'array',
					items: {
						type: 'object',
						properties: {
							fileHash: { type: 'string' },
							fileName: { type: 'string' },
							url: { type: 'string' },
							// Older drops carry responsive pairs instead of a single url.
							desktop: { type: 'string' },
							mobile: { type: 'string' },
						},
					},
					minItems: 0,
					maxItems: 10,
					// No `required` on purpose. It used to sit here, on the ARRAY, where ajv ignores it, so
					// it never ran. Moving it into `items` is not the fix: assets filled in from game data
					// arrive with a url and a fileName but no fileHash, and older drops carry desktop /
					// mobile instead of url, so requiring the trio would reject saves that work today.
				},
				youtubeURL: {
					type: 'string',
					pattern: `${emptyRe}|${youtubeUrlPattern}`,
					errorMessage: {
						pattern: 'Please enter valid YouTube video URL.',
						required: 'Please enter YouTube video URL.',
					},
				},
				gameDescriptionBody: {
					type: 'string',
					maxLength: GAME_DESCRIPTION_MAX_LENGTH,
					errorMessage: {
						maxLength: `Game description shouldn't be longer than ${GAME_DESCRIPTION_MAX_LENGTH} characters.`,
						required: 'Please enter game description.',
					},
				},
				cta: {
					type: 'string',
					errorMessage: {
						required: 'Please enter CTA.',
					},
				},
				linkMarketingCopy: {
					type: 'string',
					maxLength: LINK_MARKETING_COPY_MAX_LENGTH,
					errorMessage: {
						maxLength: `Share message shouldn't be longer than ${LINK_MARKETING_COPY_MAX_LENGTH} characters.`,
					},
				},
				// `properties` was misspelled here ("properites"), so ajv skipped the whole block and the
				// destination url was never validated server side. `custom` also had a bare string where a
				// schema belongs, which would have made the schema itself invalid once the key was read.
				// The pattern allows an empty value and does not require a protocol, which is what gets
				// stored (removeProtocolAndWww), so existing drops keep validating.
				url: {
					type: 'object',
					properties: {
						value: {
							type: 'string',
							pattern: `${emptyRe}|${urlPattern}`,
							maxLength: 300,
							errorMessage: {
								pattern: 'Must be proper url',
								maxLength: 'max: 300 characters',
							},
						},
						custom: { type: 'boolean' },
					},
				},
				shortUrl: {
					type: 'string',
					pattern: urlPattern,
					errorMessage: {
						pattern: 'Please enter valid URL.',
					},
				},
				viewershipBreakpoints: {
					type: 'object',
					properties: {
						twitch: {
							type: 'array',
							items: {
								type: 'object',
								properties: {
									viewershipCount: { type: 'integer' },
									maxDares: { type: 'integer' },
								},
								required: ['viewershipCount', 'maxDares'],
							},
						},
						youtube: {
							type: 'array',
							items: {
								type: 'object',
								properties: {
									viewershipCount: { type: 'integer' },
									maxDares: { type: 'integer' },
								},
								required: ['viewershipCount', 'maxDares'],
							},
						},
						tiktok: {
							type: 'array',
							items: {
								type: 'object',
								properties: {
									viewershipCount: { type: 'integer' },
									maxDares: { type: 'integer' },
								},
								required: ['viewershipCount', 'maxDares'],
							},
						},
						// The wizard has always sent four platforms; this one was simply missing here.
						youtubeShorts: {
							type: 'array',
							items: {
								type: 'object',
								properties: {
									viewershipCount: { type: 'integer' },
									maxDares: { type: 'integer' },
								},
								required: ['viewershipCount', 'maxDares'],
							},
						},
					},
				},
				dares: {
					type: 'array',
					items: {
						type: 'object',
						properties: {
							clipsOnly: { type: 'boolean' },
							disabled: { type: 'boolean' },
							title: { type: 'string' },
							description: { type: 'string', maxLength: 110 },
							level: {
								type: 'string',
								enum: Object.values(dropDareDifficultyLevelEnum),
							},
							product: {
								type: 'object',
								properties: {
									id: { type: 'string' },
									name: { type: 'string' },
									type: {
										type: 'string',
										enum: [
											RewardType.KEY,
											RewardType.CODE,
											RewardType.CUSTOM,
											RewardType.BUNDLE,
											RewardType.PLACEHOLDER,
											RewardType.EXTERNAL_API_FULFILLED,
										],
									},
								},
								required: ['id'],
							},
							game: {
								type: 'object',
								properties: {
									label: { type: 'string' },
									id: { type: 'integer' },
								},
								required: ['id'],
							},
							amount: { type: 'integer' },
							// The field the client and `daresSerializer` actually use. It was declared as
							// `rewardsReserved`, a name nothing writes. Left as a plain number rather than an
							// integer so a dare stored with a fractional value cannot start failing saves.
							reservedProducts: { type: 'number' },
							hint: {
								type: 'string',
								maxLength: DARE_HINT_MAX_LENGTH,
							},
							rewardFor: {
								type: 'string',
								enum: rewardForOptions.map(option => option.value),
							},
							estimatedTime: {
								type: 'string',
							},
							// These three used to sit OUTSIDE `properties`, as siblings of it, where they were
							// silently ignored. `platform` was also the wrong name: every writer and reader
							// uses `platforms` (plural). Values are restricted to DarePlatform by the form
							// schemas on the client; deliberately not enumerated here, so that a legacy dare
							// carrying an unknown platform cannot make an otherwise fine save fail.
							platforms: {
								type: 'array',
								items: { type: 'string' },
							},
							authPlatform: { type: 'string' },
							expirationDate: { type: 'string' },
						},
						required: ['title', 'description', 'product', 'amount', 'level'],
					},
				},
				campaignThumbnail: {
					type: ['string', 'object'],
				},
				campaignProduct: {
					type: 'string',
					errorMessage: {
						required: 'Please enter your product name.',
					},
				},
				isMultiGame: { type: 'boolean' },
				awardPoints: { type: 'boolean' },
				pointsLimit: { type: 'number' },
				pointsStreamLimit: { type: 'number' },
				pointsAwarded: { type: 'number' },
				isPrivate: { type: 'boolean' },
				pointsCalculationMethod: {
					type: 'string',
					enum: [pointsCalculationMethodEnum.DEFAULT, pointsCalculationMethodEnum.CPVH],
				},
				costPerViewHourPoints: { type: 'number', min: 0, max: 500 },
			},
			required: ['campaignName'],
			additionalProperties: false,
		},
		publish: { type: 'boolean' },
	},
}
